import React, { useEffect } from 'react'
import { connect, type MapStateToProps } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { TouchableRipple } from 'react-native-paper'
import styled, { useTheme } from 'styled-components/native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { RootState } from 'APP/Store/CreateStore'
import WellBeingRedux from 'APP/Redux/WellBeingRedux'
import { selectIsWellBeingIndexAssessmentEligible } from 'APP/Sagas/WellBeingSagas'
import { getNameDisplay } from 'APP/Lib/ProfileHelpers'
import I18n from 'APP/I18n'
import Analytics from 'APP/Services/Analytics'
import Config from 'APP/Config'
import Icon from 'APP/Converse/Icon'
import Logo from 'APP/Components/Logo'
import Typography from 'APP/Converse/Typography'
import { Images } from 'APP/Themes'
import { Badge } from 'APP/Nav/Components/Badge'
import useScreenView from 'APP/Hooks/useScreenView'
import type { PatientProfileState } from 'APP/Store/Patient/types'

const WellbeingIndexContainer = styled(TouchableRipple)`
  height: 44px;
  flex-direction: row;
  gap: ${({ theme }) => theme.metrics.baseMargin / 4}px;
  background-color: ${({ theme }) => theme.colors.buttonProfile};
  border-radius: ${({ theme }) => theme.metrics.borderRadius.large};
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  align-items: center;
`

const WellbeingActionsContainer = styled.View`
  display: flex;
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
  flex-direction: row;
  align-items: center;
`

const WellbeingProfileContainer = styled(TouchableRipple)`
  background-color: ${({ theme }) => theme.colors.buttonProfile};
  border-radius: ${({ theme }) => theme.metrics.borderRadius.large};
  height: 44px;
  width: 44px;
  align-items: center;
  justify-content: center;
`

const TodayHeaderContainer = styled.View`
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  gap: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
`

const TodayHeaderTopRowContainer = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: ${({ theme: { isBigScreen } }) => (isBigScreen ? 'flex-end' : 'space-between')};
`
export const PositionedBadge = styled(Badge)`
  left: 11px;
`

interface StateProps {
  hasWellBeingIndexAssessmentEligibility: boolean
  profile: PatientProfileState
}

type OwnProps = Record<string, never>

type Props = StateProps & OwnProps

const TodayHeader = ({ profile, hasWellBeingIndexAssessmentEligibility }: Props) => {
  const theme = useTheme()
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const { isBigScreen } = useScreenView()

  const { givenName, preferredName, pronouns } = profile
  const wellBeingAssessment = useSelector((state) => state.wellBeingAssessment)

  const unreadCount = useSelector((state) => state.zendesk.unreadCount)
  const { nameDisplay } = getNameDisplay(givenName, preferredName)
  const showGreeting = Config.FEATURES?.SHOW_TODAY_GREETING ?? true
  const renderPronouns = pronouns && showGreeting

  const getLatestWellBeingAssessment = () => dispatch(WellBeingRedux.getLatest())

  useEffect(() => {
    getLatestWellBeingAssessment()
  }, [])

  const handleAccountPress = () => {
    Analytics.trackEvent('button_click', { button_value: 'TodayScreen.profile' })
    navigation.navigate('account')
  }

  const handleWbiPress = () => {
    Analytics.trackEvent('button_click', { button_value: 'TodayScreen.wbi' })
    navigation.navigate('wellBeingIndex')
  }

  return (
    <TodayHeaderContainer>
      <TodayHeaderTopRowContainer>
        {!isBigScreen && <Logo source={Images.logoBlue} />}
        <WellbeingActionsContainer>
          {hasWellBeingIndexAssessmentEligibility && (
            <WellbeingIndexContainer
              borderless
              onPress={handleWbiPress}
              testID="WBI_HEADER_ACTION"
              accessibilityLabel={I18n.t('AccessibilityIcons.wellbeingIndex')}
            >
              <>
                <Icon
                  variant="materialIcons"
                  src="insights"
                  color="todayHeaderActionIcon"
                  size={theme.metrics.icon.xsmall}
                />

                {!wellBeingAssessment.latest?.loading && (
                  <Typography variant="bodySmallBold" color="secondaryText">
                    {wellBeingAssessment.latest.data?.score || I18n.t('HomeScreen.featured')}
                  </Typography>
                )}
              </>
            </WellbeingIndexContainer>
          )}
          <WellbeingProfileContainer
            borderless
            onPress={handleAccountPress}
            accessibilityLabel={I18n.t('AccountScreen.title')}
            accessibilityRole="button"
            testID="WBI_PROFILE_ICON"
          >
            <>
              {unreadCount > 0 && <PositionedBadge />}
              <Icon variant="svg" src={Images.groups} color="todayHeaderActionIcon" />
            </>
          </WellbeingProfileContainer>
        </WellbeingActionsContainer>
      </TodayHeaderTopRowContainer>
      {showGreeting && (
        <Typography variant="h1" numberOfLines={1} testID="TODAY_SCREEN_HEADER_TITLE">
          {I18n.t('TodayScreen.welcome', { name: nameDisplay })}
        </Typography>
      )}
      {renderPronouns && (
        <Typography variant="bodyLarge" numberOfLines={1}>
          {pronouns}
        </Typography>
      )}
    </TodayHeaderContainer>
  )
}

const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => {
  return {
    profile: state.patient?.profile,
    hasWellBeingIndexAssessmentEligibility: selectIsWellBeingIndexAssessmentEligible(state),
  }
}

export default connect(mapStateToProps, null)(TodayHeader)
